import { compareDatesPredicate, DateTime } from "@bps/date-time";
import { CalendarEventType } from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { CoreStore } from "@stores/core/CoreStore.ts";
import { OrgUnit } from "@stores/core/models/OrgUnit.ts";
import { PracOrgUnit } from "@stores/practice/models/PracOrgUnit.ts";
import { PracticeStore } from "@stores/practice/PracticeStore.ts";
import { RootStore } from "@stores/root/RootStore.ts";

export const fetchAllLocations = async (
  core: CoreStore,
  practice: PracticeStore
): Promise<OrgUnit[]> => {
  const { results: locations } = await core.loadOrgUnits();
  const locationData = await practice.getAllOrgUnitsLocationData();

  if (locations.length) {
    const orgUnits = locations
      .filter(l => locationData.find(data => data.id === l.id))
      .sort((a, b) =>
        compareDatesPredicate(b.createdLocationDate, a.createdLocationDate)
      );
    await Promise.all(
      orgUnits.map(unit => unit.preloadIsDefaultLocation(unit.id))
    );
    return orgUnits;
  }
  return [];
};

export interface OrgUnitScreenData {
  pracOrgUnit: PracOrgUnit;
  orgUnit: OrgUnit;
  hasAppointments: boolean;
  hasMeetings: boolean;
  hasUnavailables: boolean;
  hasFutureWaitingListEntries: boolean;
}

export const fetchOrgUnitScreenData = async (
  root: RootStore,
  locationId: string
): Promise<OrgUnitScreenData> => {
  const [pracOrgUnit, orgUnit] = await Promise.all([
    root.practice.getOrgUnit(locationId),
    root.core.getOrgUnit(locationId)
  ]);

  if (!pracOrgUnit) {
    throw new Error("Could not locate Prac Org Unit");
  }

  let hasAppointments = false;
  let hasMeetings = false;
  let hasUnavailables = false;

  if (pracOrgUnit.isDefault) {
    return {
      pracOrgUnit,
      orgUnit,
      hasAppointments,
      hasMeetings,
      hasUnavailables,
      hasFutureWaitingListEntries: false
    };
  }

  // No need to check for appointments or waiting list entries if location is default

  const futureAppointmentsPromise = root.booking.getFutureCalendarEvents({
    orgUnitIds: [locationId]
  });

  const hasWaitingListEntriesPromise = root.booking
    .getLocationWaitingListItems(locationId)
    .then(result =>
      result.some(
        x => DateTime.fromISO(x.until) > DateTime.today().minus({ days: 1 })
      )
    );

  const [futureAppointments, hasFutureWaitingListEntries] = await Promise.all([
    futureAppointmentsPromise,
    hasWaitingListEntriesPromise
  ]);

  futureAppointments.forEach(calendarEvent => {
    if (calendarEvent.type === CalendarEventType.Meeting) {
      hasMeetings = true;
    } else if (calendarEvent.type === CalendarEventType.Unavailable) {
      hasUnavailables = true;
    } else {
      hasAppointments = true;
    }
  });

  return {
    pracOrgUnit,
    orgUnit,
    hasAppointments,
    hasMeetings,
    hasUnavailables,
    hasFutureWaitingListEntries
  };
};
